import { z } from 'zod';
import { EHandlebars } from '../headers_footers/shared';
import { ETemplateStyles } from '../types';
import { Obituary, ObituaryFilingTypeNames } from '../types/obituary';

import { Layout } from '../types/layout';
import { wrapError, wrapSuccess } from '../types/responses';
import { getInlineCSSStylesFromCrop } from './images';
import {
  adobeInDesignJustificationToCSS,
  generateObjectStylesCSS,
  generateParagraphStylesCSS
} from './styles';
import { OrderFilingType } from '../types/filingType';
import { LayoutModel } from '../model/objects/layoutModel';
import { ModularSizeModel } from '../model/objects/modularSizeModel';
import {
  ClassifiedFilingTypeNames,
  ForSaleClassified,
  isForSaleClassified,
  isMilestoneClassified,
  MilestonesClassified
} from '../types/classified';
import { DEFAULT_TEMPLATE_STYLES } from '../types/templates';
import { Ad, isObituary } from '../types/ad';
import { NewspaperOrderModel } from '../model/objects/newspaperOrderModel';
import {
  FREEFORM_IMAGE,
  FREEFORM_IMAGE_BOTTOM_TEXT,
  FREEFORM_IMAGE_TEXT_ROW,
  FREEFORM_IMAGE_TOP_TEXT,
  MODULAR_TEXT_ONLY,
  MODULAR_TWO_IMAGE_TEXT_ROW,
  MODULAR_TWO_IMAGE_TOP_TEXT
} from './modular-layouts';
import { getDynamicSections } from './dynamic-sections';

export const DEFAULT_DPI_VALUES = {
  INDESIGN: 72,
  PUPPETEER: 96,
  PRINT: 300
};

// TODO: Pass in styles to header from template styles
const headerBlockMarkup = `
  <style>
    {{objectStylesCSS}}
    {{paragraphStylesCSS}}
  </style>
  {{#with (getDynamicSection dynamicSections "header")}}
    <header class="{{objectClass}}">
      <h1 class="{{textClass}}">{{{content}}}</h1>
    </header>
  {{/with}}
`;

const bodyContentMarkup = `
  {{#if title }}
    <h1 style="{{{titleStyles}}}">{{{title}}}</h1>
  {{/if}}

  {{#if logo }}
    <div id="ad-logo-container" style="float: left;">
      <div id="ad-logo-buffer"></div>
      <img id="ad-logo" src="{{{logo}}}" style="height: 0.5in; width: auto; object-fit: contain; margin: 4px;" />
    </div>
  {{/if}}

  <section id="ad-content" style="{{{textStyles}}}; font-weight:normal;">
    {{{content}}}
  </section>
`;

const baseMarkup = `
  ${headerBlockMarkup}
  ${bodyContentMarkup}
`;

// TODO(goodpaul): Move this to the db
export const ONE_COL_TEXT_ONLY: Layout = {
  tag: 'default',
  id: 'one-col-death-notice',
  handlebarTemplate: baseMarkup,
  columns: 1,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716673316156/template_a_normal.svg',
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none'
};

const ONE_COL_TEXT_ONLY_NO_HEADER: Layout = {
  tag: 'default',
  id: 'one-col-text-only-no-header',
  handlebarTemplate: bodyContentMarkup,
  columns: 1,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1741102880354/no_header.svg',
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none',
  titleConfig: { hidden: true }
};

const TWO_COL_TEXT_ONLY: Layout = {
  tag: 'default',
  id: 'two-col-death-notice',
  handlebarTemplate: baseMarkup,
  columns: 2,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716673363185/template_g_normal.svg',
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none'
};

const THREE_COL_TEXT_ONLY: Layout = {
  tag: 'default',
  id: 'three-col-text-only',
  handlebarTemplate: baseMarkup,
  columns: 3,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716673363185/template_g_normal.svg',
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none'
};

const TWO_COL_TWO_COL_COUNT_TEXT_ONLY: Layout = {
  tag: 'default',
  id: 'two-col-two-col-count-text-only-split-2-cols',
  handlebarTemplate: bodyContentMarkup,
  columns: 2,
  columnCount: 2,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1741203281797/4split_image.svg',
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none'
};

export const DEFAULT_DEATH_NOTICE_LAYOUTS = [
  ONE_COL_TEXT_ONLY,
  TWO_COL_TEXT_ONLY
];

export const ONE_COL_ONE_PHOTO_VERTICAL: Layout = {
  tag: 'default',
  id: 'one-col-3-4-image-1-photo',
  handlebarTemplate: `
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 1,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716478716163/template_sv_normal.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

export const ONE_COL_ONE_PHOTO_HORIZONTAL: Layout = {
  tag: 'default',
  id: 'one-col-4-3-image-1-photo',
  handlebarTemplate: `
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 1,
  photos: 1,
  iconSvgUrl:
    'https://cdn.column.us/custom-documents%2Fpermalink%2F1738700761540%2Ftemplate_sv_normal_1.svg',
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%'
};

export const ONE_COL_ONE_PHOTO_BOTTOM_VERTICAL: Layout = {
  tag: 'default',
  id: 'one-col-3-4-image-1-photo-bottom',
  handlebarTemplate: `
    ${bodyContentMarkup}
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
  `,
  columns: 1,
  images: [{ position: 'bottom' }],
  iconSvgUrl:
    'https://cdn.column.us/custom-documents%2Fpermalink%2F1736955362191%2F1_image_bottom.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

export const ONE_COL_ONE_PHOTO_BOTTOM_HORIZONTAL: Layout = {
  tag: 'default',
  id: 'one-col-4-3-image-1-photo-bottom',
  handlebarTemplate: `
    ${bodyContentMarkup}
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
  `,
  columns: 1,
  images: [{ position: 'bottom' }],
  iconSvgUrl:
    'https://cdn.column.us/custom-documents%2Fpermalink%2F1738700916983%2Ftemplate_sv_normal_2.svg',
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%'
};

export const ONE_COL_TWO_PHOTO_TOP_BOTTOM_VERTICAL: Layout = {
  tag: 'default',
  id: 'one-col-3-4-image-1-photo-top-bottom',
  handlebarTemplate: `
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
    ${bodyContentMarkup}
    {{#if secondHeaderImage }}
      <div>
        <img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}};" />
      </div>
    {{/if}}
  `,
  columns: 1,
  images: [{ position: 'top' }, { position: 'bottom' }],
  iconSvgUrl:
    'https://cdn.column.us/custom-documents%2Fpermalink%2F1736955993794%2F2_image_top_bottom.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

export const ONE_COL_TWO_PHOTO_TOP_BOTTOM_HORIZONTAL: Layout = {
  tag: 'default',
  id: 'one-col-4-3-image-1-photo-top-bottom',
  handlebarTemplate: `
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
    ${bodyContentMarkup}
    {{#if secondHeaderImage }}
      <div>
        <img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}};" />
      </div>
    {{/if}}
  `,
  columns: 1,
  images: [{ position: 'top' }, { position: 'bottom' }],
  iconSvgUrl:
    'https://cdn.column.us/custom-documents%2Fpermalink%2F1736955993794%2F2_image_top_bottom.svg',
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%'
};

export const TEMPLATE_S_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-4-3-image-1-photo',
  handlebarTemplate: `
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 3,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716474500733/template_s_normal.svg',
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%'
};

const TEMPLATE_SV_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-3-4-image-1-photo',
  handlebarTemplate: `
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 3,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716478716163/template_sv_normal.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

export const SCNG_AGENT_LAYOUT: Layout = {
  tag: 'default',
  id: 'scng-agent-layout',
  handlebarTemplate: `
    <style>
      * {
        box-sizing: border-box;
      }
      .container {
        display: flex;
        height: 100%;
      }
      .content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      .primary-image {
        height: 100%;
        aspect-ratio: 3 / 4;
      }
      .secondary-image {
        height: 50px;
        padding-bottom: 10px;
      }
      .secondary-image img {
        height: 100%;
      }
      h1 p {
        margin-top: 7pt;
        margin-bottom: 0;
      }
    </style>
    <div class="container">
      {{#if images.[0].url }}
        <div class="primary-image">
          <img src="{{{images.[0].url}}}" style="{{{images.[0].styles}}};" />
        </div>
      {{/if}}
      
      <div class="content">
        <div>
          {{#if title }}
            <h1 style="{{{titleStyles}}}; margin-top: 0; text-transform: uppercase;">{{{title}}}</h1>
          {{/if}}

          <section id="ad-content" style="{{{textStyles}}};">
            {{{content}}}
          </section>
        </div>
        {{#if images.[1].url }}
          <div class="secondary-image">
            <img src="{{{images.[1].url}}}" />
          </div>
        {{/if}}
      </div>
    </div>`,
  columns: 3,
  images: [
    { position: 'top', aspectRatio: '3 / 4' },
    { position: 'bottom', aspectRatio: 'none', optional: true }
  ],
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1768240814571/g_normal.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

export const TEMPLATE_T_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-4-3-image-2-photo',
  handlebarTemplate: `
    {{#if headerImage }}
      <div style="display: flex;">
        <div style="flex:1;"><img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" /></div>
        <div style="flex:1;"><img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}};" /></div>
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 3,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716478847918/template_t_normal.svg',
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%'
};

const TEMPLATE_TV_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-3-4-image-2-photo',
  handlebarTemplate: `
    {{#if headerImage }}
      <div style="display: flex;">
        <div style="flex:1;"><img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" /></div>
        <div style="flex:1;"><img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}};" /></div>
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 3,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716478958991/template_tv_normal.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

const TEMPLATE_U_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-4-3-image-1-photo-variant-2-big-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 60%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 3,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716479060868/template_u_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '4 / 3',
  photoWidth: '50%'
};

const TEMPLATE_UV_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-3-4-image-1-photo-variant-2-big-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 60%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 3,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716479145916/template_uv_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '50%'
};

const TEMPLATE_V_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-4-3-image-1-photo-variant-2-small-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 40%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 3,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716479425509/template_v_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '4 / 3',
  photoWidth: '30%'
};

const TEMPLATE_VV_LAYOUT: Layout = {
  tag: 'default',
  id: 'three-col-3-4-image-1-photo-variant-2-small-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 40%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 3,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716555885314/template_vv_normal__1_.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '30%'
};

// FOUR COLS
const TEMPLATE_VC4NP_LAYOUT: Layout = {
  tag: 'default',
  id: 'four-col-no-photo',
  handlebarTemplate: bodyContentMarkup,
  columns: 4,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716559894275/template_vc5np_normal.svg',
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none'
};

const FOUR_COL_NO_PHOTO_FOUR_COLS_LAYOUT: Layout = {
  tag: 'default',
  id: 'four-col-no-photo-split-four-cols',
  handlebarTemplate: bodyContentMarkup,
  columns: 4,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1741203662857/4colsplit.svg',
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none',
  columnCount: 4
};

const TEMPLATE_VC41PV_LAYOUT: Layout = {
  tag: 'default',
  id: 'four-col-3-4-image-1-photo-skinny-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <div id="float-right-image-container">
          <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 30%; float: right;" />
        </div>
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 4,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716559512112/template_vc41pv_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '30%'
};

const FOUR_COL_ONE_PHOTO_FOUR_COLS_LAYOUT: Layout = {
  tag: 'default',
  id: 'four-col-one-photo-four-cols-split-four-columns',
  handlebarTemplate: `
  {{#if headerImage }}
    <img id="center-left-image-container" src="{{{headerImage}}}" style="{{{headerImageStyles}}}; float: right;" />

    <img id="center-right-image-container" src="{{{headerImage}}}" style="{{{headerImageStyles}}}; float: left;" />

  {{/if}}
  ${bodyContentMarkup}`,
  columns: 4,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1746815699487/fourcol_center.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%',
  columnCount: 4
};

const TEMPLATE_VC41PH_LAYOUT: Layout = {
  tag: 'default',
  id: 'four-col-4-3-image-1-photo-skinny-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 30%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 4,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716559576942/template_vc41ph_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '4 / 3',
  photoWidth: '30%'
};

const TEMPLATE_VC42PV_LAYOUT: Layout = {
  tag: 'default',
  id: 'four-col-3-4-image-2-photo',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <div id="float-right-image-container">
          <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 25%; float: right;" />
        </div>
      {{/if}}
      {{#if secondHeaderImage }}
        <img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}}; width: 25%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 4,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716559710646/template_vc42pv_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '25%'
};

const TEMPLATE_VC42PH_LAYOUT: Layout = {
  tag: 'default',
  id: 'four-col-4-3-image-2-photo',
  handlebarTemplate: `
    <div id="float-right-image-container" style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 25%; float: right;" />
      {{/if}}
      {{#if secondHeaderImage }}
        <img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}}; width: 25%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 4,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716560593311/template_vc42ph_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '4 / 3',
  photoWidth: '25%'
};

// FIVE COLS
const TEMPLATE_VC51PV_LAYOUT: Layout = {
  tag: 'default',
  id: 'five-col-3-4-image-1-photo-skinny-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 20%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 5,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716559983071/template_vc51pv_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '20%'
};

const TEMPLATE_VC51PH_LAYOUT: Layout = {
  tag: 'default',
  id: 'five-col-4-3-image-1-photo-skinny-rhs',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 20%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 5,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716560055432/template_vc51ph_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '4 / 3',
  photoWidth: '20%'
};

const TEMPLATE_VC52PV_LAYOUT: Layout = {
  tag: 'default',
  id: 'five-col-3-4-image-2-photo',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 20%; float: right;" />
      {{/if}}
      {{#if secondHeaderImage }}
        <img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}}; width: 20%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 5,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716560101529/template_vc52pv_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '20%'
};

const TEMPLATE_VC52PH_LAYOUT: Layout = {
  tag: 'default',
  id: 'five-col-4-3-image-2-photo',
  handlebarTemplate: `
    <div style="font-weight:normal; overflow: hidden;">
      {{#if headerImage }}
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 25%; float: right;" />
      {{/if}}
      {{#if secondHeaderImage }}
        <img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}}; width: 25%; float: right;" />
      {{/if}}
      ${bodyContentMarkup}
    </div>`,
  columns: 5,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716560152267/template_vc52ph_normal.svg',
  photoAlignment: 'right',
  aspectRatio: '4 / 3',
  photoWidth: '25%'
};

const TEMPLATE_VC5NP_LAYOUT: Layout = {
  tag: 'default',
  id: 'five-col-no-photo',
  handlebarTemplate: bodyContentMarkup,
  columns: 5,
  photos: 0,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716559894275/template_vc5np_normal.svg',
  photoAlignment: 'center',
  aspectRatio: 'none',
  photoWidth: 'none'
};

const TEMPLATE_2COL1PH_LAYOUT: Layout = {
  tag: 'default',
  id: 'two-col-one-photo',
  handlebarTemplate: `
    {{#if headerImage }}
      <div>
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" />
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 2,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents%2Fpermalink%2F367f.d4956-Template-S-Normal.svg',
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: 'none'
};

export const TEMPLATE_2COL1PHV2_LAYOUT: Layout = {
  tag: 'default',
  id: 'two-col-one-photo-3-4-image',
  handlebarTemplate: `
    {{#if headerImage }}
      <div id="float-right-image-container">
        <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 50%; float: right;" />
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 2,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents%2Fpermalink%2F7bd6.404dc-Template-H-Normal.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '50%'
};

const TEMPLATE_2COL_2COLCOUNT_TOPRIGHTIMAGE_LAYOUT: Layout = {
  tag: 'default',
  id: 'two-col-split-two-col-one-photo-3-4-image',
  handlebarTemplate: `
    {{#if headerImage }}
      <img id="float-right-image-container" src="{{{headerImage}}}" style="{{{headerImageStyles}}}; width: 100%; float: right;" />
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 2,
  columnCount: 2,
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1741206240990/2col_split_image.svg',
  photoAlignment: 'right',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

const TEMPLATE_2COL2PHV1_LAYOUT: Layout = {
  tag: 'default',
  id: 'two-col-2-photo-3-4-image',
  handlebarTemplate: `
    {{#if headerImage }}
      <div style="display: flex;">
        <div style="flex:1;"><img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" /></div>
        <div style="flex:1;"><img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}};" /></div>
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 2,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents%2Fpermalink%2Fef82.19618-Template-J-Normal.svg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

const TEMPLATE_2COL2PHV2_LAYOUT: Layout = {
  tag: 'default',
  id: 'two-col-2-photo-4-3-image',
  handlebarTemplate: `
    {{#if headerImage }}
      <div style="display: flex;">
        <div style="flex:1;"><img src="{{{headerImage}}}" style="{{{headerImageStyles}}};" /></div>
        <div style="flex:1;"><img src="{{{secondHeaderImage}}}" style="{{{secondImageStyles}}};" /></div>
      </div>
    {{/if}}
    ${bodyContentMarkup}`,
  columns: 2,
  photos: 2,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents%2Fpermalink%2F4eb6.02e6b-Template-K-Normal.svg',
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%'
};

/**
 * Base display layouts
 * These are used for the display ad layouts below, with the only change being the image and id
 */
const DISPLAY_ONE_3_4_IMAGE_ONLY_LAYOUT: Layout = {
  tag: 'display',
  id: 'display-one-3-4-image-only',
  bodyConfig: { hidden: true },
  titleConfig: { hidden: true, placeholder: 'Ad title (for accessibility)' },
  handlebarTemplate: `
    <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; object-fit: cover; width: 100%;">
`,
  description: 'Image only, one column wide',
  columns: 1,
  photos: 1,
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%',
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1729780980453/one_col_3_4_template.svg'
};
const DISPLAY_TWO_COL_4_3_IMAGE_ONLY_LAYOUT: Layout = {
  tag: 'display',
  id: 'display-two-col-4-3-image-only',
  description: 'Image only, two columns wide',
  bodyConfig: { hidden: true },
  titleConfig: { hidden: true, placeholder: 'Ad title (for accessibility)' },
  handlebarTemplate: `
    <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; object-fit: cover; width: 100%;">`,
  columns: 2,
  photos: 1,
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%',
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1730126534716/onecol_43.svg'
};

/**
 * Modular sizing layouts that are:
 * 1) Image only
 * 2) Set to predefined heights in relation to the total template
 * 3) Have a single photo
 */
const getStandardImageOnlyLayoutTemplate = (
  size: keyof z.infer<
    typeof OrderContentHandlebarDataSchema
  >['standardOrderSizes']
) => `
  <img src="{{{headerImage}}}" style="object-fit: contain; width: 100%; height: {{standardOrderSizes.${size}}}px">;
`;
const BASE_STANDARD_IMAGE_ONLY_LAYOUT: Pick<
  Layout,
  | 'tag'
  | 'bodyConfig'
  | 'photos'
  | 'iconSvgUrl'
  | 'photoAlignment'
  | 'aspectRatio'
  | 'photoWidth'
  | 'titleConfig'
> = {
  tag: 'display',
  bodyConfig: { hidden: true },
  titleConfig: { hidden: true },
  photos: 1,
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1716478716163/template_sv_normal.svg',
  photoAlignment: 'center',
  aspectRatio: 'none',
  photoWidth: 'none'
};

const EIGHT_COL_FULL_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'eight-col-full-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('pageHeightInPixels'),
  columns: 8,
  description: 'Eight columns full page'
};
const SIX_COL_FULL_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'six-col-full-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('pageHeightInPixels'),
  columns: 6,
  description: 'Six columns full page'
};
const FOUR_COL_FULL_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'four-col-full-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('pageHeightInPixels'),
  columns: 4,
  description: 'Four columns full page'
};
const THREE_COL_FULL_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'three-col-full-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('pageHeightInPixels'),
  columns: 3,
  description: 'Three columns full page'
};
const TWO_COL_FULL_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'two-col-full-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('pageHeightInPixels'),
  columns: 2,
  description: 'Two columns full page'
};

const EIGHT_COL_HALF_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'eight-col-half-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('halfHeightInPixels'),
  columns: 8,
  description: 'Eight columns half page'
};
const SIX_COL_HALF_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'six-col-half-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('halfHeightInPixels'),
  columns: 6,
  description: 'Six columns half page'
};
const FOUR_COL_HALF_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'four-col-half-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('halfHeightInPixels'),
  columns: 4,
  description: 'Four columns half page'
};
const THREE_COL_HALF_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'three-col-half-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('halfHeightInPixels'),
  columns: 3,
  description: 'Three columns half page'
};
const TWO_COL_HALF_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'two-col-half-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate('halfHeightInPixels'),
  columns: 2,
  description: 'Two columns half page'
};
const TWO_COL_QUARTER_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'two-col-quarter-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate(
    'quarterHeightInPixels'
  ),
  columns: 2,
  description: 'Two columns quarter page'
};
const THREE_COL_QUARTER_HEIGHT_LAYOUT: Layout = {
  ...BASE_STANDARD_IMAGE_ONLY_LAYOUT,
  id: 'three-col-quarter-height',
  handlebarTemplate: getStandardImageOnlyLayoutTemplate(
    'quarterHeightInPixels'
  ),
  columns: 3,
  description: 'Three columns quarter page'
};

const IMAGE_ONLY_DISPLAY_LAYOUTS = [
  DISPLAY_TWO_COL_4_3_IMAGE_ONLY_LAYOUT,
  DISPLAY_ONE_3_4_IMAGE_ONLY_LAYOUT,
  EIGHT_COL_FULL_HEIGHT_LAYOUT,
  SIX_COL_FULL_HEIGHT_LAYOUT,
  FOUR_COL_FULL_HEIGHT_LAYOUT,
  THREE_COL_FULL_HEIGHT_LAYOUT,
  TWO_COL_FULL_HEIGHT_LAYOUT,
  EIGHT_COL_HALF_HEIGHT_LAYOUT,
  SIX_COL_HALF_HEIGHT_LAYOUT,
  FOUR_COL_HALF_HEIGHT_LAYOUT,
  THREE_COL_HALF_HEIGHT_LAYOUT,
  TWO_COL_HALF_HEIGHT_LAYOUT,
  TWO_COL_QUARTER_HEIGHT_LAYOUT,
  THREE_COL_QUARTER_HEIGHT_LAYOUT
];

const DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Omit<
  Layout,
  'id' | 'iconSvgUrl'
> = {
  tag: 'display',
  description: 'One column, image with text bottom right',
  handlebarTemplate: `
  <div>
    <img
      src="{{{headerImage}}}"
      style="{{{headerImageStyles}}}; object-fit: cover; width: 100%;">
    <div style="max-width: 80%; margin-left: 10%; border-radius: 2px; overflow: hidden; background-color: #fafafa; position: absolute; bottom: 0; right: 0; margin: 10px; padding: 5px;">
      <div style="border-radius: 5px; overflow: hidden;">
        <h1 style="{{{titleStyles}}}">{{{title}}}</h1>
        <div style="{{{textStyles}}};font-weight:normal">
          {{{content}}}
        </div>
      </div>
    </div>
  </div>`,
  columns: 1,
  photos: 1,
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};
const TWO_COL_SIDEWAYS_DISPLAY_LAYOUT: Omit<Layout, 'id' | 'iconSvgUrl'> = {
  tag: 'display',
  description: 'Two columns, dark background',
  handlebarTemplate: `
    <div style="background-color: black; display: flex;">
      <div
        style="color: #fafafa; flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 3; padding: 10px; background: linear-gradient(135deg, #1e3c72, #2c3e50, #2f3640)">
        <h1 style="{{{titleStyles}}};">
          {{{title}}}
        </h1>
        <div style="{{{textStyles}}};font-weight:normal; color: white;">
          {{{content}}}
        </div>
      </div>
      <div style="flex: 2;">
        <img
          src="{{{headerImage}}}"
          style="{{{headerImageStyles}}}; object-fit: cover; width: 100%; height: 100%;"/>
      </div>
    </div>
  </div>`,
  columns: 2,
  photos: 1,
  photoAlignment: 'center',
  photoWidth: '100%',
  aspectRatio: '4 / 3'
};
const TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT: Omit<Layout, 'id' | 'iconSvgUrl'> = {
  tag: 'display',
  description: 'Two columns, image with text bottom right',
  handlebarTemplate: `
  <div>
    <img
      src="{{{headerImage}}}"
      style="{{{headerImageStyles}}}; object-fit: cover; width: 100%;">
    <div style="max-width: 70%; border-radius: 2px; overflow: hidden; background-color: #fafafa; position: absolute; bottom: 0; right: 0; margin: 10px; padding: 5px;">
      <div style="border-radius: 5px; overflow: hidden;">
        <h1 style="{{{titleStyles}}}">{{{title}}}</h1>
        <div style="{{{textStyles}}};font-weight:normal">
          {{{content}}}
        </div>
      </div>
    </div>
  </div>`,
  columns: 2,
  photos: 1,
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%'
};

/**
 * Vehicles
 */
const VEHICLE_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'vehicle-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729013042917/car1col.jpg'
};
const VEHICLE_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'vehicle-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729004522662/car.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const VEHICLE_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'vehicle-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729013013469/car_2col.jpg'
};
export const VEHICLE_SALE_DISPLAY_LAYOUTS = [
  VEHICLE_TWO_COL_SIDEWAYS_LAYOUT,
  VEHICLE_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  VEHICLE_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Furniture
 */
const COUCH_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'furniture-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034236970/couchone.jpg'
};
const COUCH_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'couch-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034255605/couchside.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const COUCH_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'couch-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034269567/2colright.jpg'
};
const FURNITURE_DISPLAY_LAYOUTS = [
  COUCH_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  COUCH_TWO_COL_SIDEWAYS_LAYOUT,
  COUCH_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Electronics
 */
const ELECTRONICS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'electronics-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034182181/onecollaptop.jpg'
};
const ELECTRONICS_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'electronics-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034207895/split2collaptop.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const ELECTRONICS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'electronics-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034220782/laptopbottomright.jpg'
};
const ELECTRONICS_DISPLAY_LAYOUTS = [
  ELECTRONICS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  ELECTRONICS_TWO_COL_SIDEWAYS_LAYOUT,
  ELECTRONICS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Home appliances
 */
const HOME_APPLIANCES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'home-appliances-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034141222/washingmachineonecol.jpg'
};
const HOME_APPLIANCES_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'home-appliances-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034157204/washingmachine2colsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const HOME_APPLIANCES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'home-appliances-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034169966/wash2colbottomright.jpg'
};
const HOME_APPLIANCES_DISPLAY_LAYOUTS = [
  HOME_APPLIANCES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  HOME_APPLIANCES_TWO_COL_SIDEWAYS_LAYOUT,
  HOME_APPLIANCES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Clothing and apparel
 */
const CLOTHING_AND_APPAREL_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'apparel-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034078095/apparelonecol.jpg'
};
const CLOTHING_AND_APPAREL_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'apparel-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034103160/twocolapparelsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const CLOTHING_AND_APPAREL_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'apparel-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034122260/twocolbottomright.jpg'
};
const CLOTHING_AND_APPAREL_DISPLAY_LAYOUTS = [
  CLOTHING_AND_APPAREL_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  CLOTHING_AND_APPAREL_TWO_COL_SIDEWAYS_LAYOUT,
  CLOTHING_AND_APPAREL_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Sporting goods
 */
const SPORTING_GOODS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'sporting-goods-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034030257/sportsonecol.jpg'
};
const SPORTING_GOODS_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'sporting-goods-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034047349/sports2colsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const SPORTING_GOODS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'sporting-goods-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034059843/sports2colbottomright.jpg'
};
const SPORTING_GOODS_DISPLAY_LAYOUTS = [
  SPORTING_GOODS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  SPORTING_GOODS_TWO_COL_SIDEWAYS_LAYOUT,
  SPORTING_GOODS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Tools
 */
const TOOLS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'tools-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033983109/toolsonecol.jpg'
};
const TOOLS_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'tools-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033997937/tools2colsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const TOOLS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'tools-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729034012399/tools2colbottomright.jpg'
};
const TOOLS_DISPLAY_LAYOUTS = [
  TOOLS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  TOOLS_TWO_COL_SIDEWAYS_LAYOUT,
  TOOLS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Toys and games
 */
const TOYS_AND_GAMES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'toys-and-games-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033939188/toysonecol.jpg'
};
const TOYS_AND_GAMES_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'toys-and-games-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033950464/toys2colsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const TOYS_AND_GAMES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'toys-and-games-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033962489/toys2colbottomright.jpg'
};
const TOYS_AND_GAMES_DISPLAY_LAYOUTS = [
  TOYS_AND_GAMES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  TOYS_AND_GAMES_TWO_COL_SIDEWAYS_LAYOUT,
  TOYS_AND_GAMES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Musical instruments
 */
const MUSICAL_INSTRUMENTS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'musical-instruments-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033906936/musiconecol.jpg'
};
const MUSICAL_INSTRUMENTS_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'musical-instruments-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033916700/pianotwocolsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const MUSICAL_INSTRUMENTS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'musical-instruments-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033927684/pianotwocolbottomright.jpg'
};
const MUSICAL_INSTRUMENTS_DISPLAY_LAYOUTS = [
  MUSICAL_INSTRUMENTS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  MUSICAL_INSTRUMENTS_TWO_COL_SIDEWAYS_LAYOUT,
  MUSICAL_INSTRUMENTS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Books
 */
const BOOKS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'books-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033858745/collegetextbooksonecol.jpg'
};
const BOOKS_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'books-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033870991/twocolsplitbooks.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const BOOKS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'books-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033884500/twocolbottomrightbooks.jpg'
};
const BOOKS_DISPLAY_LAYOUTS = [
  BOOKS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  BOOKS_TWO_COL_SIDEWAYS_LAYOUT,
  BOOKS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Jewelry and Accessories
 */
const JEWELRY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'jewe-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033799077/onecolwatches.jpg'
};
const JEWELRY_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'jewe-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033820005/twocolwatchsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const JEWELRY_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'jewe-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033842746/watchestwocolbottomright.jpg'
};
const JEWELRY_DISPLAY_LAYOUTS = [
  JEWELRY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  JEWELRY_TWO_COL_SIDEWAYS_LAYOUT,
  JEWELRY_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Art and collectibles
 */
const ART_AND_COLLECTIBLES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'art-and-collectibles-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033753599/onecolart.jpg'
};
const ART_AND_COLLECTIBLES_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'art-and-collectibles-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033772283/arttwocolsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const ART_AND_COLLECTIBLES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'art-and-collectibles-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033786385/arttwocolbottomright.jpg'
};
const ART_AND_COLLECTIBLES_DISPLAY_LAYOUTS = [
  ART_AND_COLLECTIBLES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  ART_AND_COLLECTIBLES_TWO_COL_SIDEWAYS_LAYOUT,
  ART_AND_COLLECTIBLES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Camera and photography equipment
 */
const CAMERA_AND_PHOTOGRAPHY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'camera-and-photography-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033715486/cameraonecol.jpg'
};
const CAMERA_AND_PHOTOGRAPHY_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'camera-and-photography-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033729619/cameratwocolsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const CAMERA_AND_PHOTOGRAPHY_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'camera-and-photography-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033741463/twocolcamerabottomright.jpg'
};
const CAMERA_AND_PHOTOGRAPHY_DISPLAY_LAYOUTS = [
  CAMERA_AND_PHOTOGRAPHY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  CAMERA_AND_PHOTOGRAPHY_TWO_COL_SIDEWAYS_LAYOUT,
  CAMERA_AND_PHOTOGRAPHY_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Home decor
 */
const HOME_DECOR_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'home-decor-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033672731/decoronecolumn.jpg'
};
const HOME_DECOR_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'home-decor-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033692237/twocoldecorsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const HOME_DECOR_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'home-decor-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033704048/decortwocolbottomright.jpg'
};
const HOME_DECOR_DISPLAY_LAYOUTS = [
  HOME_DECOR_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  HOME_DECOR_TWO_COL_SIDEWAYS_LAYOUT,
  HOME_DECOR_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Baby items
 */
const BABY_ITEMS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'baby-items-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033632782/babybundleonecol.jpg'
};
const BABY_ITEMS_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'baby-items-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033647312/babytwocolsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const BABY_ITEMS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'baby-items-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033658053/babytwocolbottomright.jpg'
};
const BABY_ITEMS_DISPLAY_LAYOUTS = [
  BABY_ITEMS_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  BABY_ITEMS_TWO_COL_SIDEWAYS_LAYOUT,
  BABY_ITEMS_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Health and beauty products
 */
const HEALTH_BEAUTY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'health-beauty-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729018327010/healthbeauty.jpg'
};
const HEALTH_BEAUTY_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'health-beauty-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729018327010/healthbeauty.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const HEALTH_BEAUTY_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'health-beauty-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729018327010/healthbeauty.jpg'
};
const HEALTH_BEAUTY_DISPLAY_LAYOUTS = [
  HEALTH_BEAUTY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  HEALTH_BEAUTY_TWO_COL_SIDEWAYS_LAYOUT,
  HEALTH_BEAUTY_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Outdoor equipment
 */
const OUTDOOR_EQUIPMENTONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'outdoor-equipment-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033593216/hikingonecol.jpg'
};
const OUTDOOR_EQUIPMENTTWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'outdoor-equipment-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033602705/hikingtwocolsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const OUTDOOR_EQUIPMENTTWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'outdoor-equipment-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033614442/hiking2colbottomright.jpg'
};
const OUTDOOR_EQUIPMENT_DISPLAY_LAYOUTS = [
  OUTDOOR_EQUIPMENTONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  OUTDOOR_EQUIPMENTTWO_COL_SIDEWAYS_LAYOUT,
  OUTDOOR_EQUIPMENTTWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

/**
 * Antiques
 */
const ANTIQUES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...DISPLAY_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  id: 'antiques-one-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033541989/antiqueonecol.jpg'
};
const ANTIQUES_TWO_COL_SIDEWAYS_LAYOUT: Layout = {
  id: 'antiques-two-col-sideways-dark-background',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033569949/antiquetwocolsplit.jpg',
  ...TWO_COL_SIDEWAYS_DISPLAY_LAYOUT
};
const ANTIQUES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'antiques-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729033580072/antiquetwocolbottomright.jpg'
};
const ANTIQUES_DISPLAY_LAYOUTS = [
  ANTIQUES_ONE_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  ANTIQUES_TWO_COL_SIDEWAYS_LAYOUT,
  ANTIQUES_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT
];

const FOR_SALE_DISPLAY_LAYOUTS = [
  ...VEHICLE_SALE_DISPLAY_LAYOUTS,
  ...FURNITURE_DISPLAY_LAYOUTS,
  ...ELECTRONICS_DISPLAY_LAYOUTS,
  ...HOME_APPLIANCES_DISPLAY_LAYOUTS,
  ...CLOTHING_AND_APPAREL_DISPLAY_LAYOUTS,
  ...SPORTING_GOODS_DISPLAY_LAYOUTS,
  ...TOOLS_DISPLAY_LAYOUTS,
  ...TOYS_AND_GAMES_DISPLAY_LAYOUTS,
  ...MUSICAL_INSTRUMENTS_DISPLAY_LAYOUTS,
  ...BOOKS_DISPLAY_LAYOUTS,
  ...JEWELRY_DISPLAY_LAYOUTS,
  ...ART_AND_COLLECTIBLES_DISPLAY_LAYOUTS,
  ...CAMERA_AND_PHOTOGRAPHY_DISPLAY_LAYOUTS,
  ...HOME_DECOR_DISPLAY_LAYOUTS,
  ...BABY_ITEMS_DISPLAY_LAYOUTS,
  ...HEALTH_BEAUTY_DISPLAY_LAYOUTS,
  ...OUTDOOR_EQUIPMENT_DISPLAY_LAYOUTS,
  ...ANTIQUES_DISPLAY_LAYOUTS
];

/**
 * Housing and property
 */
const HOUSING_TWO_COL_SPLIT: Layout = {
  tag: 'display',
  id: 'housing-two-col-split',
  handlebarTemplate: `
    <div style="background-color: #fafafa; display: flex;">
      <div
        style="color: #4a4a4a; flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 3; padding: 10px; background: linear-gradient(135deg, #f0f0f0, #e0e0e0, #d0d0d0);">
        <h1 style="{{{titleStyles}}};">
          {{{title}}}
        </h1>
        <div style="{{{textStyles}}};font-weight:normal; color: #4a4a4a;">
          {{{content}}}
        </div>
      </div>
      <div style="flex: 2;">
        <img
          src="{{{headerImage}}}"
          style="{{{headerImageStyles}}}; object-fit: cover; width: 100%; height: 100%;"/>
      </div>
    </div>
  </div>`,
  columns: 2,
  photos: 1,
  photoAlignment: 'center',
  aspectRatio: '4 / 3',
  photoWidth: '100%',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729089285836/housing2colsplit.jpg'
};
const HOUSING_DISPLAY_LAYOUTS = [HOUSING_TWO_COL_SPLIT];

/**
 * Services
 */

export const TWO_COL_BUSINESS_CARD: Layout = {
  tag: 'display',
  id: 'two-col-business-card',
  description: 'Business card',
  handlebarTemplate: `
    <img src="{{{headerImage}}}" style="{{{headerImageStyles}}}; object-position: center; object-fit: cover; width: 100%;">
  `,
  bodyConfig: { hidden: true },
  titleConfig: { hidden: true },
  columns: 2,
  images: [{ position: 'top' }],
  photoAlignment: 'center',
  aspectRatio: '3.5 / 2',
  iconSvgUrl:
    'https://enotice-production.imgix.net/custom-documents/permalink/1730126534716/onecol_43.svg',
  photoWidth: '100%'
};

const DARK_BACKGROUND_BUSINESS_CARD_LAYOUT: Layout = {
  tag: 'display',
  id: 'dark-background-services-two-col',
  description: 'Two column dark background',
  handlebarTemplate: `
    <div style="background-color: #fafafa; display: flex;">
      <div
        style="color: #4a4a4a; flex: 2; display: flex; flex-direction: column; justify-content: center; gap: 3; padding: 10px; background: linear-gradient(90deg, #f0fcff, #d0f4ff);">
        <h1 style="{{{titleStyles}}};">
          {{{title}}}
        </h1>
        <div style="{{{textStyles}}};font-weight:normal; color: #4a4a4a;">
          {{{content}}}
        </div>
      </div>
      <div style="flex: 1;">
        <img
          src="{{{headerImage}}}"
          style="{{{headerImageStyles}}}; object-fit: cover; width: 100%; height: 100%; object-position: top;"/>
      </div>
    </div>
  </div>`,
  columns: 2,
  photos: 1,
  photoAlignment: 'right',
  aspectRatio: '4 / 3',
  photoWidth: '50%',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729093794767/cleaning2col.jpg'
};
const SERVICES_LAYOUTS = [
  ...IMAGE_ONLY_DISPLAY_LAYOUTS,
  DARK_BACKGROUND_BUSINESS_CARD_LAYOUT
];

/**
 * Engagements
 */
export const ENGAGEMENT_PAPER_CARD_LAYOUT: Layout = {
  tag: 'display',
  id: 'engagement-two-col-paper-card',
  handlebarTemplate: `
    <div style="background-color: #FBFAF5; display: flex;">
      <div
        style="color: #4a4a4a; flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 3; padding: 20px;">
        <h1 style="{{{titleStyles}}}; text-align: center;">
          {{{title}}}
        </h1>
        <div style="{{{textStyles}}};font-weight:normal; color: #4a4a4a;">
          {{{content}}}
        </div>
      </div>
    </div>
  </div>`,
  columns: 2,
  photos: 0,
  photoAlignment: 'none',
  aspectRatio: 'none',
  photoWidth: 'none',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729090695926/engagement.jpg'
};

const MILESTONE_LAYOUTS = [ENGAGEMENT_PAPER_CARD_LAYOUT];

/**
 * Pet
 */
const PET_ONE_COL_IMAGE_CONTENT_SPLIT_TOP_BOTTOM: Layout = {
  tag: 'display',
  id: 'pet-one-col-image-text-top-bottom',
  handlebarTemplate: `
  <div>
    <div style="width: 80%; margin-left: 10%; border-radius: 2px; overflow: hidden; background-color: #fafafa; position: absolute; top: 0; margin-top: 10px; padding: 5px;">
      <div style="border-radius: 5px; overflow: hidden;">
        <h1 style="{{{titleStyles}}}">{{{title}}}</h1>
      </div>
    </div>
    <img
      src="{{{headerImage}}}"
      style="{{{headerImageStyles}}}; object-fit: cover; width: 100%;">
    <div style="width: 80%; margin-left: 10%; border-radius: 2px; overflow: hidden; background-color: #fafafa; position: absolute; bottom: 0; margin-bottom: 10px; padding: 5px;">
      <div style="border-radius: 5px; overflow: hidden;">
        <div style="{{{textStyles}}};font-weight:normal">
          {{{content}}}
        </div>
      </div>
    </div>
  </div>`,
  columns: 1,
  photos: 1,
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729091068416/onecoldogsplit.jpg',
  photoAlignment: 'center',
  aspectRatio: '3 / 4',
  photoWidth: '100%'
};

const PET_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT: Layout = {
  ...TWO_COL_BOTTOM_RIGHT_DISPLAY_LAYOUT,
  id: 'pet-two-col-image-text-bottom-right',
  iconSvgUrl:
    'https://res.cloudinary.com/dgqq2xsfd/image/upload/enotice-production/custom-documents/permalink/1729090983645/dogtwocolbottomright.jpg'
};

export const DEFAULT_CLASSIFIED_LAYOUTS = [
  TEMPLATE_2COL1PHV2_LAYOUT,
  TEMPLATE_2COL1PH_LAYOUT,
  TEMPLATE_2COL2PHV1_LAYOUT,
  TEMPLATE_2COL2PHV2_LAYOUT,
  TEMPLATE_S_LAYOUT,
  TEMPLATE_T_LAYOUT
];

export const PET_DISPLAY_LAYOUTS = [
  PET_TWO_COL_IMAGE_CONTENT_BOTTOM_RIGHT,
  PET_ONE_COL_IMAGE_CONTENT_SPLIT_TOP_BOTTOM
];
export const CLASSIFIED_DISPLAY_LAYOUTS: Partial<
  Record<ClassifiedFilingTypeNames, Layout[]>
> = {
  [ClassifiedFilingTypeNames.ForSale]: VEHICLE_SALE_DISPLAY_LAYOUTS,
  [ClassifiedFilingTypeNames.Pets]: PET_DISPLAY_LAYOUTS
};

export const DEFAULT_OBITUARY_LAYOUTS = [
  TEMPLATE_SV_LAYOUT,
  TEMPLATE_TV_LAYOUT,
  TEMPLATE_U_LAYOUT,
  TEMPLATE_UV_LAYOUT,
  TEMPLATE_V_LAYOUT,
  TEMPLATE_VV_LAYOUT,
  TEMPLATE_VC4NP_LAYOUT,
  TEMPLATE_VC41PV_LAYOUT,
  TEMPLATE_VC41PH_LAYOUT,
  TEMPLATE_VC42PV_LAYOUT,
  TEMPLATE_VC42PH_LAYOUT,
  TEMPLATE_VC51PV_LAYOUT,
  TEMPLATE_VC51PH_LAYOUT,
  TEMPLATE_VC52PV_LAYOUT,
  TEMPLATE_VC52PH_LAYOUT,
  TEMPLATE_VC5NP_LAYOUT
];

export const SELECTABLE_LAYOUTS: Array<Layout> = [
  ...DEFAULT_DEATH_NOTICE_LAYOUTS,
  THREE_COL_TEXT_ONLY,
  TEMPLATE_2COL_2COLCOUNT_TOPRIGHTIMAGE_LAYOUT,
  FOUR_COL_ONE_PHOTO_FOUR_COLS_LAYOUT,
  ONE_COL_TEXT_ONLY_NO_HEADER,
  ONE_COL_ONE_PHOTO_VERTICAL,
  ONE_COL_ONE_PHOTO_HORIZONTAL,
  ONE_COL_ONE_PHOTO_BOTTOM_VERTICAL,
  ONE_COL_ONE_PHOTO_BOTTOM_HORIZONTAL,
  ONE_COL_TWO_PHOTO_TOP_BOTTOM_VERTICAL,
  ONE_COL_TWO_PHOTO_TOP_BOTTOM_HORIZONTAL,
  SCNG_AGENT_LAYOUT,
  ...DEFAULT_CLASSIFIED_LAYOUTS,
  ...DEFAULT_OBITUARY_LAYOUTS,
  ...FOR_SALE_DISPLAY_LAYOUTS,
  ...PET_DISPLAY_LAYOUTS,
  ...HOUSING_DISPLAY_LAYOUTS,
  ...MILESTONE_LAYOUTS,
  ...SERVICES_LAYOUTS,
  TWO_COL_BUSINESS_CARD,
  FOUR_COL_NO_PHOTO_FOUR_COLS_LAYOUT,
  TWO_COL_TWO_COL_COUNT_TEXT_ONLY,
  FREEFORM_IMAGE,
  FREEFORM_IMAGE_TOP_TEXT,
  FREEFORM_IMAGE_BOTTOM_TEXT,
  FREEFORM_IMAGE_TEXT_ROW,
  MODULAR_TEXT_ONLY,
  MODULAR_TWO_IMAGE_TOP_TEXT,
  MODULAR_TWO_IMAGE_TEXT_ROW
];

const inDesignPtToPx = (pt: number) => {
  return (pt * DEFAULT_DPI_VALUES.PUPPETEER) / DEFAULT_DPI_VALUES.INDESIGN;
};

/**
 * Gets title styles based on the titleParagraphStyle replacing any missing values with content from the overall template
 */
const getTitleStyles = (templateStyle: ETemplateStyles) => {
  const { titleParagraphStyle } = templateStyle;
  const leading = titleParagraphStyle?.leading ?? templateStyle.leading;
  return [
    `min-height:${titleParagraphStyle?.fontSize ?? templateStyle.fontSize}pt`,
    `margin-left:${titleParagraphStyle?.marginLeft ?? 0}pt`,
    `margin-right:${titleParagraphStyle?.marginRight ?? 0}pt`,
    titleParagraphStyle?.spaceBefore
      ? `margin-top:${titleParagraphStyle.spaceBefore}pt`
      : '',
    titleParagraphStyle?.spaceAfter
      ? `margin-bottom:${titleParagraphStyle.spaceAfter}pt`
      : '',
    `padding:0pt`,
    ...Object.entries(
      adobeInDesignJustificationToCSS(titleParagraphStyle?.justification)
    ).map(([prop, value]) => `${prop}:${value}`),
    `font-family:${titleParagraphStyle?.font ?? templateStyle.font}`,
    `font-size:${titleParagraphStyle?.fontSize ?? templateStyle.fontSize}pt`,
    `line-height:${leading ? `${leading}pt` : '120%'}`,
    'overflow-wrap:break-word',
    titleParagraphStyle?.decoration
      ? `text-decoration:${titleParagraphStyle.decoration}`
      : '',
    titleParagraphStyle?.underline ? 'text-decoration:underline' : '',
    'letter-spacing:0',
    'font-smooth:never',
    'word-break:break-word',
    `font-weight:${titleParagraphStyle?.fontWeight ?? 'bold'}`
  ]
    .filter(Boolean)
    .join(';');
};

/**
 * Applies any non-inherited styles to each paragraph in the ad content. inherited styles are applied to the parent in getTextStyles
 * @param {string} adContent - The ad content to apply styles to
 * @param {DOMParser} DOMparser - The DOMParser to use to parse
 * @param {ETemplateStyles} - The styles to apply to the paragraphs. The supported styles are padding, firstLineIndent, and hyphenation
 * @returns The ad content with the styles applied
 */
const applyStylesToParagraphs = async (
  adContent: string | undefined,
  DOMparser: typeof DOMParser,
  { padding, firstLineIndent, hyphenation }: ETemplateStyles
) => {
  if (!adContent) {
    return adContent;
  }
  const doc = new DOMparser().parseFromString(adContent, 'text/html');
  const paragraphs = doc.querySelectorAll('p');
  paragraphs.forEach((p: NodeListOf<HTMLElement>[any], index) => {
    // eslint-disable-next-line no-param-reassign
    p.style.padding = `
      ${index === 0 ? 0 : inDesignPtToPx(padding?.top ?? 0)}px 
      ${inDesignPtToPx(padding?.right ?? 0)}px
      ${inDesignPtToPx(padding?.bottom ?? 0)}px
      ${inDesignPtToPx(padding?.left ?? 0)}px`;
    // eslint-disable-next-line no-param-reassign
    p.style.margin = '0px';
    // eslint-disable-next-line no-param-reassign
    p.style.textIndent = `${firstLineIndent ?? 0}px`;
    if (hyphenation) {
      // eslint-disable-next-line no-param-reassign
      p.style.hyphens = 'auto';
    }
  });

  return doc.body.innerHTML;
};

const getTextStyles = (
  layout: Layout,
  {
    fontSize,
    font,
    leading,
    textInset,
    justification,
    columnGutter
  }: ETemplateStyles
) => {
  let marginValue = '0';
  if (textInset) {
    const textInsetPx = inDesignPtToPx(textInset);
    marginValue = `${textInsetPx}`;
  }
  const styles = [
    `min-height:${fontSize}pt`,
    `margin:${marginValue}px`,
    `padding:0pt`,
    ...Object.entries(adobeInDesignJustificationToCSS(justification)).map(
      ([prop, value]) => `${prop}:${value}`
    ),
    `font-family:${font}`,
    `font-size:${fontSize}pt`,
    `line-height:${leading ? `${leading}pt` : '120%'}`,
    'overflow-wrap:break-word',
    'letter-spacing:0',
    'font-smooth:never',
    'word-break:break-word'
  ];
  if (layout.columnCount && layout.columnCount > 1) {
    styles.push(`column-gap:${columnGutter || 0}px`);
    styles.push(`column-count:${layout.columnCount}`);
  }
  return styles.join(';');
};

const OrderContentHandlebarDataSchema = z.object({
  // New layouts should use `images` array rather than `headerImage` and `secondHeaderImage` for flexibility and extensibility
  images: z.array(
    z.object({
      url: z.string(),
      styles: z.string().optional()
    })
  ),
  headerImage: z.string().optional().nullable(),
  headerImageStyles: z.string().optional().nullable(),

  secondHeaderImage: z.string().optional().nullable(),
  secondImageStyles: z.string().optional().nullable(),

  dynamicSections: z.array(
    z.object({
      id: z.string(),
      content: z.string().optional(),
      textClass: z.string().optional(),
      objectClass: z.string().optional()
    })
  ),
  titleStyles: z.string(),
  textStyles: z.string(),
  content: z.string().optional(),
  title: z.string().optional(),
  logo: z.string().optional().nullable(),
  standardOrderSizes: z.object({
    pageHeightInPixels: z.number(),
    halfHeightInPixels: z.number(),
    quarterHeightInPixels: z.number(),
    eighthHeightInPixels: z.number()
  }),
  backgroundColor: z.string(),
  objectStylesCSS: z.string(),
  paragraphStylesCSS: z.string()
});
type OrderContentHandlebarData = z.infer<
  typeof OrderContentHandlebarDataSchema
>;

/**
 * Dynamically determine the template that we should use for a particular obituary order.
 * We re-determine the handlebar value instead of using the value cached on the object because
 * it may have changed since the object was created.
 */
const getHandlebarStringForLayout = (layout: Layout) => {
  const relevantTemplate = findLayoutById(layout.id);
  if (!relevantTemplate) {
    return wrapError(new Error('No template found for layout'));
  }
  return wrapSuccess(relevantTemplate.handlebarTemplate);
};

/**
 * Determine the width of an ad based on the layout and template styles. Consider the following layout:
 * | column |gutter| column |gutter| column |gutter| column |gutter| column |gutter| column |
 * Our templates don't have the column width directly, so we need to calculate it. In order to figure out
 * the width of a single column, we need to subtract the total gutter width from the page width, then divide by the
 * number of columns to get the width of a single column.
 * Once we have the width of a single column, we can multiply it by the number of columns to get the total width of the ad
 * *without gutters*. The final step is to add the total gutter width to the column width to get the final size.
 * Before exporting, we take the final step of scaling by the default DPI of puppeteer (96) over the default DPI of
 * Indesign (72).
 */
export const getAdWidth = (params: {
  layout: Layout;
  templateStyles: ETemplateStyles;
  modularSize?: ModularSizeModel | null;
}) => {
  const { layout, templateStyles, modularSize } = params;

  // If modular size is provided, use its width directly
  if (modularSize) {
    return wrapSuccess(modularSize.widthInPixels());
  }

  const { columns } = layout;
  if (columns < 1) {
    return wrapError(new Error('Invalid number of columns'));
  }
  if (
    !templateStyles.pageWidth ||
    !templateStyles.columnGutter ||
    !templateStyles.columnCount
  ) {
    return wrapError(new Error('Invalid template styles'));
  }
  const totalGutterWidth =
    templateStyles.columnGutter * (templateStyles.columnCount - 1);

  const pageWidthWithoutGutter = templateStyles.pageWidth - totalGutterWidth;

  const columnWidth = pageWidthWithoutGutter / templateStyles.columnCount;

  const adWidthFromColumns = columnWidth * layout.columns;
  const adWidthFromGutter = templateStyles.columnGutter * (columns - 1);

  const adWidth = adWidthFromColumns + adWidthFromGutter;

  const scaledAdWidth =
    adWidth * (DEFAULT_DPI_VALUES.PUPPETEER / DEFAULT_DPI_VALUES.INDESIGN);
  return wrapSuccess(scaledAdWidth);
};

const getWidthRestrictedUrl = (
  originalUrl: string,
  cdnTransformations: { maxImageWidth: number; isGrayscale: boolean }
) =>
  originalUrl.replace(
    '/q_auto:best/',
    `/q_auto:best/c_limit,w_${cdnTransformations.maxImageWidth}/${
      cdnTransformations.isGrayscale ? 'e_grayscale/' : ''
    }`
  );

/**
 * Exposes standardized styles that can be used in layouts to determine the size of the ad.
 * This makes it easy for publishers to enable a "half page ad" or a "quarter page ad"
 */
export const getStandardOrderHeights = (templateStyles: ETemplateStyles) => {
  const { pageHeight } = templateStyles;
  if (!pageHeight) {
    return wrapError(new Error('Invalid template styles'));
  }

  const pageHeightInInches = pageHeight / DEFAULT_DPI_VALUES.INDESIGN;

  const pageHeightInPixels = pageHeightInInches * DEFAULT_DPI_VALUES.PUPPETEER;

  const halfHeightInInches = Math.floor(pageHeightInInches) / 2;

  const halfHeightInPixels = halfHeightInInches * DEFAULT_DPI_VALUES.PUPPETEER;

  const quarterHeightInInches = Math.floor(halfHeightInInches) / 2;

  const quarterHeightInPixels =
    quarterHeightInInches * DEFAULT_DPI_VALUES.PUPPETEER;
  const eighthHeightInInches = Math.floor(quarterHeightInInches) / 2;

  const eighthHeightInPixels =
    eighthHeightInInches * DEFAULT_DPI_VALUES.PUPPETEER;

  return wrapSuccess({
    pageHeightInPixels,
    halfHeightInPixels,
    quarterHeightInPixels,
    eighthHeightInPixels
  });
};

// Provides the maximum width in pixels that an image should be based on the column count, page width, and print quality PPI (300)
function calculateImageMaxWidth(
  layout: Layout,
  templateStyles: ETemplateStyles
) {
  const columnCount = layout.columns;
  const gutterCount = columnCount > 1 ? columnCount - 1 : 0;
  const pageWidthInInches = templateStyles.pageWidth
    ? templateStyles.pageWidth / DEFAULT_DPI_VALUES.INDESIGN
    : 0;
  const gutterWidthInInches = templateStyles.columnGutter
    ? templateStyles.columnGutter / DEFAULT_DPI_VALUES.INDESIGN
    : 0;
  const totalGutterWidthInInches = gutterCount * gutterWidthInInches;
  const totalColumnWidthInInches = pageWidthInInches - totalGutterWidthInInches;
  const singleColumnWidthInInches = totalColumnWidthInInches / columnCount;
  const adWidthInInches =
    singleColumnWidthInInches * columnCount + totalGutterWidthInInches;
  const columnWidthInPixels = adWidthInInches * DEFAULT_DPI_VALUES.PRINT;
  return Math.ceil(columnWidthInPixels);
}

// TODO: Figure out what we want to do around placeholders in previews
const PLACEHOLDER_IMG = 'https://placehold.co/600x400?text=Preview';
export const layoutOrderContent = async (
  newspaperOrder: NewspaperOrderModel,
  adContent: Partial<Obituary>,
  templateStyles: ETemplateStyles,
  parser: typeof DOMParser
) => {
  const { layout, colorOptions } = newspaperOrder.modelData;
  const { response: handlebarString, error: handlebarError } =
    getHandlebarStringForLayout(layout);
  if (handlebarError) {
    return wrapError(handlebarError);
  }
  const handlebarTemplate =
    EHandlebars.compile<OrderContentHandlebarData>(handlebarString);

  const [standardOrderSizesError, standardOrderSizes] =
    getStandardOrderHeights(templateStyles);
  if (standardOrderSizesError) {
    return wrapError(standardOrderSizesError);
  }

  const firstOrderImage = adContent.orderImages?.[0];
  const firstOrderImageUrl = firstOrderImage?.imageUrl || adContent.headerImage;
  const secondOrderImage = adContent.orderImages?.[1];
  const secondOrderImageUrl =
    secondOrderImage?.imageUrl || adContent.secondHeaderImage;

  // If we render full quality images, the PDF size maybe too large to download in the browser, so we limit the overall max width to a quality that is hopefully acceptable for print but not allowing excessively large images.
  const maxImageWidth = calculateImageMaxWidth(layout, {
    ...DEFAULT_TEMPLATE_STYLES,
    ...templateStyles
  });
  const cdnTransformations = {
    maxImageWidth,
    isGrayscale: Boolean(colorOptions?.isGrayscale)
  };

  const [dynamicSectionsError, dynamicSections] = await getDynamicSections(
    newspaperOrder,
    adContent
  );

  if (dynamicSectionsError) {
    return wrapError(dynamicSectionsError);
  }

  const objectStylesCSS = generateObjectStylesCSS(templateStyles);
  const paragraphStylesCSS = generateParagraphStylesCSS(templateStyles);

  const textStyles = getTextStyles(layout, templateStyles);

  const layoutData = findLayoutById(newspaperOrder.modelData.layout.id);
  const images =
    adContent.orderImages?.map((orderImage, index) => {
      const { imageUrl } = orderImage;
      const imageConfig = layoutData?.images?.[index];
      const placeholder = imageConfig?.optional ? '' : PLACEHOLDER_IMG;

      return {
        url: imageUrl
          ? getWidthRestrictedUrl(imageUrl, cdnTransformations)
          : placeholder,
        styles: getInlineCSSStylesFromCrop(
          orderImage.crop,
          templateStyles.photoStyleProperties,
          layout.images
            ? imageConfig?.aspectRatio || layout.aspectRatio
            : layout.aspectRatio
        )
      };
    }) || [];

  const htmlContent = handlebarTemplate({
    title: adContent.title,
    dynamicSections,
    content: await applyStylesToParagraphs(
      adContent.content,
      parser,
      templateStyles
    ),
    images,
    secondHeaderImage: secondOrderImageUrl
      ? getWidthRestrictedUrl(secondOrderImageUrl, cdnTransformations)
      : PLACEHOLDER_IMG,
    headerImage: firstOrderImageUrl
      ? getWidthRestrictedUrl(firstOrderImageUrl, cdnTransformations)
      : PLACEHOLDER_IMG,
    titleStyles: getTitleStyles(templateStyles),
    textStyles,
    headerImageStyles: getInlineCSSStylesFromCrop(
      firstOrderImage?.crop || adContent.headerImageCrop,
      templateStyles.photoStyleProperties,
      layout.images
        ? layout.images[0]?.aspectRatio || layout.aspectRatio
        : layout.aspectRatio
    ),
    secondImageStyles: getInlineCSSStylesFromCrop(
      secondOrderImage?.crop || adContent.secondImageCrop,
      templateStyles.photoStyleProperties,
      layout.images
        ? layout.images[1]?.aspectRatio || layout.aspectRatio
        : layout.aspectRatio
    ),
    logo: adContent.funeralHomeLogo ?? null,
    standardOrderSizes,
    backgroundColor:
      (!colorOptions?.isGrayscale && colorOptions?.backgroundColor) ||
      'transparent',
    objectStylesCSS,
    paragraphStylesCSS
  });

  return wrapSuccess({ htmlContent });
};

/**
 * Sets the min-height of the text section to the height of the image to ensure
 * that we don't have a layout where the text section is shorter than the image.
 * Genesis: https://columnpbc.atlassian.net/browse/ONCALL-4611
 */
export function setMinTextSectionHeight() {
  const img = document.querySelector('img');
  const textSection = document.querySelector('section');
  if (!img || !textSection) return;
  const imgBoundingBox = img.getBoundingClientRect();
  const textSectionBoundingBox = textSection.getBoundingClientRect();
  textSection.style.minHeight = `${
    imgBoundingBox.bottom -
    textSectionBoundingBox.top -
    parseFloat(textSection.style.marginBottom || '0')
  }px`;
}

/**
 * Inserts logos into the bottom right of ad content
 * This is called directly in the renderDocument function in headlessBrowserModel.ts
 */
export function fixLogoPositioning() {
  let resultLog = '';
  const MAX_LOGO_POSITION_SEARCH_ITERATIONS = 100;
  const LOGO_CONTAINER_HEIGHT_INCREMENT = 2;

  resultLog += 'fixLogoPositioning called\n';

  const logoContainer = document.querySelector(
    '#ad-logo-container'
  ) as HTMLElement;

  if (!logoContainer) return;
  logoContainer.style.clear = 'right';

  const logoBuffer = logoContainer.querySelector(
    '#ad-logo-buffer'
  ) as HTMLElement;
  if (!logoBuffer) return;

  resultLog += 'logoBuffer found\n';

  const logoImg = logoContainer.querySelector('#ad-logo') as HTMLElement;

  const floatRightImageContainer = document.querySelector(
    '#float-right-image-container'
  ) as HTMLElement;
  let floatingImageHeight = 0;
  if (floatRightImageContainer) {
    resultLog += 'floatRightImageContainer found\n';
    const floatingImage = floatRightImageContainer.querySelector('img');
    if (floatingImage) {
      floatingImageHeight = floatingImage.clientHeight;
      resultLog += `floatingImageHeight: ${floatingImageHeight}\n`;
    }
  }

  resultLog += `floatingImageHeight: ${floatingImageHeight}\n`;

  const textElement = document.querySelector('#ad-content');
  if (!textElement) return;

  resultLog += 'textElement found\n';

  const setTransparentHeight = (transparentHeight: number) => {
    logoBuffer.style.height = `${transparentHeight}px`;

    // set the height of the box wrapper to be the height of the text
    logoContainer.style.height = `${
      textElement.clientHeight - floatingImageHeight
    }px`;

    logoContainer.style.shapeOutside = `polygon(0px ${transparentHeight}px, ${logoContainer.clientWidth}px ${transparentHeight}px, ${logoContainer.clientWidth}px ${textElement.clientHeight}px, 0px ${textElement.clientHeight}px)`;
    logoContainer.style.float = 'right';
  };

  // set transparent height to be the height of the total text subtracting the black box height
  let transparentHeightSearchValue = Math.max(
    textElement.clientHeight - logoContainer.clientHeight - floatingImageHeight,
    0
  );
  for (let i = 0; i < MAX_LOGO_POSITION_SEARCH_ITERATIONS; i++) {
    setTransparentHeight(transparentHeightSearchValue);

    const logoBoundingBox = logoImg.getBoundingClientRect();
    const textElementBoundingBox = textElement.getBoundingClientRect();

    resultLog += `logoBoundingBox.bottom: ${logoBoundingBox.bottom}\n`;
    resultLog += `textElementBoundingBox.bottom: ${textElementBoundingBox.bottom}\n`;

    if (logoBoundingBox.bottom < textElementBoundingBox.bottom) {
      transparentHeightSearchValue += LOGO_CONTAINER_HEIGHT_INCREMENT;
      resultLog += `transparentHeightSearchValue: ${transparentHeightSearchValue}\n`;
    } else {
      break;
    }
  }

  // reset the height of the logo container to be the height of the logo + height of the buffer
  logoContainer.style.height = `${
    logoBuffer.clientHeight + logoImg.clientHeight
  }px`;
  resultLog += `logoContainer.style.height: ${logoContainer.style.height}\n`;
  return resultLog;
}

/**
 * Introduces text splits across the text content of the ad, and then finds the best place to insert an image
 */
export function fixImagePositioningInSplitColumns() {
  const SPLITS_PER_PARAGRAPH = 200;
  const img = document.querySelector('#float-right-image-container');
  const paragraphs = document.querySelectorAll('#ad-content p');
  if (!img || !paragraphs.length) return;
  for (const paragraph of paragraphs) {
    const textNode = paragraph.firstChild as Text;
    if (
      !textNode ||
      textNode.nodeType !== Node.TEXT_NODE ||
      !textNode.textContent
    ) {
      continue;
    }
    // find all the indexes of space or newline characters
    const allowedSplitIndexes = [];
    for (let i = 0; i < textNode.length; i++) {
      if (textNode.textContent[i] === ' ' || textNode.textContent[i] === '\n') {
        allowedSplitIndexes.push(i);
      }
    }

    const splitsToCreate = Math.min(
      allowedSplitIndexes.length,
      SPLITS_PER_PARAGRAPH
    );
    const splitIncrement = Math.floor(
      allowedSplitIndexes.length / splitsToCreate
    );
    const splitIndexes = [];
    for (let i = 1; i < splitsToCreate; i++) {
      const relevantSplitIndex = i * splitIncrement;
      const splitPosition = allowedSplitIndexes[relevantSplitIndex];
      splitIndexes.push(splitPosition);
    }

    for (const splitIndex of splitIndexes.reverse()) {
      textNode.splitText(splitIndex);
    }
  }

  let bestInsertParagraph = null;
  let bestInsertNode = null;
  let bestY = Infinity;
  let bestX = -Infinity;
  for (const paragraph of document.querySelectorAll('#ad-content p')) {
    for (const textNode of Array.from(paragraph.childNodes)) {
      if (textNode.nodeType !== Node.TEXT_NODE) {
        continue;
      }
      paragraph.insertBefore(img, textNode);
      const imgBoundingRect = img.getBoundingClientRect();

      if (imgBoundingRect.y < bestY || imgBoundingRect.x > bestX) {
        bestY = imgBoundingRect.y;
        bestX = imgBoundingRect.x;
        bestInsertNode = textNode;
        bestInsertParagraph = paragraph;
      }
      img.remove();
    }
  }
  if (bestInsertNode && bestInsertParagraph) {
    bestInsertParagraph.insertBefore(img, bestInsertNode);
  }
}

function centerDoubleImagesInLayout() {
  const leftImage = document.querySelector('#center-left-image-container');
  const rightImage = document.querySelector('#center-right-image-container');
  const adContent = document.querySelector('#ad-content');
  if (!leftImage || !rightImage || !adContent) return;
  const paragraphs = document.querySelectorAll('#ad-content p');
  const columnGutter = window.getComputedStyle(adContent).columnGap;

  const SPLIT_SIZE = 250;
  for (const paragraph of paragraphs) {
    const availableTextNodes = Array.from(paragraph.childNodes).filter(
      node => node.nodeType === Node.TEXT_NODE && node.textContent
    ) as Text[];

    for (const textNode of availableTextNodes) {
      let remainingText = textNode.textContent || '';
      let currentNode = textNode;

      while (remainingText.length > SPLIT_SIZE) {
        // Find the best split point within the next 100 characters
        let splitPosition = SPLIT_SIZE;

        while (
          splitPosition > 0 &&
          remainingText[splitPosition] !== ' ' &&
          remainingText[splitPosition] !== '\n'
        ) {
          splitPosition--;
        }

        // If no space was found, default to the hard split
        if (splitPosition === 0) {
          splitPosition = SPLIT_SIZE;
        }

        // Split the text node
        currentNode = currentNode.splitText(splitPosition);
        remainingText = currentNode.textContent || '';
      }
    }
  }

  leftImage.remove();
  rightImage.remove();
  let idxTracker = 0;
  const paragraphTextNodeArray = [];
  for (const paragraph of paragraphs) {
    for (const textNode of paragraph.childNodes) {
      paragraphTextNodeArray.push({
        idx: idxTracker,
        paragraph,
        textNode
      });
      idxTracker += 1;
    }
  }

  const validLeftPositions = Array.from(
    paragraphTextNodeArray.slice(
      Math.ceil(paragraphTextNodeArray.length * 0.25),
      Math.ceil(paragraphTextNodeArray.length * 0.6)
    )
  ).reverse();
  const validRightPositions = Array.from(
    paragraphTextNodeArray.slice(
      Math.ceil(paragraphTextNodeArray.length * 0.4),
      Math.ceil(paragraphTextNodeArray.length * 0.8)
    )
  ).reverse();

  const centerPosition =
    (adContent.getBoundingClientRect().x +
      adContent.getBoundingClientRect().width) /
    2;

  let bestLeft = null;
  let bestRight = null;

  let bestOverallScore = Infinity;

  for (const validLeftPosition of validLeftPositions) {
    for (const validRightPosition of validRightPositions) {
      if (validLeftPosition.idx > validRightPosition.idx) {
        continue;
      }

      leftImage.remove();
      rightImage.remove();
      validLeftPosition.paragraph.insertBefore(
        leftImage,
        validLeftPosition.textNode
      );
      validRightPosition.paragraph.insertBefore(
        rightImage,
        validRightPosition.textNode
      );
      const leftCandidateRect = leftImage.getBoundingClientRect();
      const rightCandidateRect = rightImage.getBoundingClientRect();

      if (leftCandidateRect.x >= rightCandidateRect.x) {
        continue;
      }

      const xDelta =
        Math.abs(
          leftCandidateRect.x + leftCandidateRect.width / 2 - centerPosition
        ) +
        Math.abs(
          rightCandidateRect.x + rightCandidateRect.width / 2 - centerPosition
        );
      const yDelta = Math.abs(leftCandidateRect.y + rightCandidateRect.y);
      const overallScore = xDelta + yDelta ** 2;

      if (overallScore < bestOverallScore) {
        bestOverallScore = overallScore;
        bestLeft = validLeftPosition;
        bestRight = validRightPosition;
      }
    }
  }
  leftImage.remove();
  rightImage.remove();

  bestLeft?.paragraph.insertBefore(leftImage, bestLeft.textNode);
  bestRight?.paragraph.insertBefore(rightImage, bestRight.textNode);
  const placedLeftImage = document.querySelector(
    '#center-left-image-container'
  ) as HTMLElement;
  const placedRightImage = document.querySelector(
    '#center-right-image-container'
  ) as HTMLElement;
  const [gutterSize, imageWidth] = [
    columnGutter,
    getComputedStyle(placedLeftImage).width
  ].map(style => parseInt(style.replace('px', ''), 10));
  placedLeftImage.style.marginRight = `-${gutterSize / 2 + imageWidth / 2}px`;
  placedRightImage.style.marginLeft = `-${gutterSize / 2 + imageWidth / 2}px`;
  placedRightImage.style.opacity = '0';
}

/**
 * Determines what javascript we need to run after the page loads to apply any fixes that we can't
 * do with CSS.
 */
export const createPostLoadScript = (layout: Layout) => {
  let scriptBody = '';
  // make sure that the photo doesn't overhang the text section
  const layoutModel = new LayoutModel(layout);
  if (layoutModel.hasImages && layout.photoAlignment !== 'center') {
    scriptBody += `
    ${setMinTextSectionHeight.toString()}
    setMinTextSectionHeight();
  `;
  }

  if (layoutModel.isSplitColumns && layout.photoAlignment === 'right') {
    scriptBody += `
    ${fixImagePositioningInSplitColumns.toString()}
    fixImagePositioningInSplitColumns();
  `;
  }

  if (layoutModel.isSplitColumns && layout.photoAlignment === 'center') {
    scriptBody += `
    ${centerDoubleImagesInLayout.toString()}
    centerDoubleImagesInLayout();
  `;
  }

  if (!scriptBody) {
    return '';
  }

  return `
<script>
window.onload = function() {
  ${scriptBody}
}
</script>
`.trim();
};

export const getLayoutsForForSaleAd = (adData: Partial<ForSaleClassified>) => {
  switch (adData.forSaleType) {
    case 'antiques': {
      return ANTIQUES_DISPLAY_LAYOUTS;
    }
    case 'art-collectibles': {
      return ART_AND_COLLECTIBLES_DISPLAY_LAYOUTS;
    }
    case 'baby-items': {
      return BABY_ITEMS_DISPLAY_LAYOUTS;
    }
    case 'books': {
      return BOOKS_DISPLAY_LAYOUTS;
    }
    case 'cameras-photography': {
      return CAMERA_AND_PHOTOGRAPHY_DISPLAY_LAYOUTS;
    }
    case 'clothing-apparel': {
      return CLOTHING_AND_APPAREL_DISPLAY_LAYOUTS;
    }
    case 'electronics': {
      return ELECTRONICS_DISPLAY_LAYOUTS;
    }
    case 'furniture': {
      return FURNITURE_DISPLAY_LAYOUTS;
    }
    case 'sporting-goods': {
      return SPORTING_GOODS_DISPLAY_LAYOUTS;
    }
    case 'health-beauty': {
      return HEALTH_BEAUTY_DISPLAY_LAYOUTS;
    }
    case 'home-appliances': {
      return HOME_APPLIANCES_DISPLAY_LAYOUTS;
    }
    case 'home-decor': {
      return HOME_DECOR_DISPLAY_LAYOUTS;
    }
    case 'jewelry-accessories': {
      return JEWELRY_DISPLAY_LAYOUTS;
    }
    case 'musical-instruments': {
      return MUSICAL_INSTRUMENTS_DISPLAY_LAYOUTS;
    }
    case 'outdoor-equipment': {
      return OUTDOOR_EQUIPMENT_DISPLAY_LAYOUTS;
    }
    case 'vehicles': {
      return VEHICLE_SALE_DISPLAY_LAYOUTS;
    }
    case 'tools': {
      return TOOLS_DISPLAY_LAYOUTS;
    }
    case 'toys-games': {
      return TOYS_AND_GAMES_DISPLAY_LAYOUTS;
    }
    default: {
      return [];
    }
  }
};

export const getLayoutsForMilestoneAd = (
  adData: Partial<MilestonesClassified>
) => {
  switch (adData.milestoneType) {
    case 'wedding': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'adoption': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'anniversary': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'baby-shower': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'baptism-christening': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'bar-bat-mitzvah': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'birth': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'engagement': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'first-communion': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'graduation': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'housewarming': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'military-service-deployment': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'moving-relocation': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'new-job': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'promotion': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'quinceanera': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    case 'retirement': {
      return [ENGAGEMENT_PAPER_CARD_LAYOUT];
    }
    default: {
      return [];
    }
  }
};

export function getDefaultLayoutsByFilingType(
  filingType: OrderFilingType,
  adData: Partial<Ad>
): Layout[] {
  if (
    filingType.label === ObituaryFilingTypeNames.DeathNotice ||
    filingType.label === ClassifiedFilingTypeNames.CommunityEngagementLetters
  ) {
    return DEFAULT_DEATH_NOTICE_LAYOUTS;
  }
  if (isForSaleClassified(adData)) {
    return [...DEFAULT_CLASSIFIED_LAYOUTS, ...getLayoutsForForSaleAd(adData)];
  }
  if (filingType.label === ClassifiedFilingTypeNames.Pets) {
    return [...DEFAULT_CLASSIFIED_LAYOUTS, ...PET_DISPLAY_LAYOUTS];
  }

  if (isObituary(adData)) {
    return [
      TWO_COL_TEXT_ONLY,
      ...DEFAULT_CLASSIFIED_LAYOUTS,
      ...DEFAULT_OBITUARY_LAYOUTS
    ];
  }
  if (isMilestoneClassified(adData)) {
    return [...DEFAULT_CLASSIFIED_LAYOUTS, ...getLayoutsForMilestoneAd(adData)];
  }
  if (filingType.label === ClassifiedFilingTypeNames.HousingProperty) {
    return [...DEFAULT_CLASSIFIED_LAYOUTS, ...HOUSING_DISPLAY_LAYOUTS];
  }
  if (filingType.label === ClassifiedFilingTypeNames.Services) {
    return [...DEFAULT_CLASSIFIED_LAYOUTS, ...SERVICES_LAYOUTS];
  }
  return [...DEFAULT_DEATH_NOTICE_LAYOUTS, ...DEFAULT_CLASSIFIED_LAYOUTS];
}

export function findLayoutById(layoutId: string): Layout | undefined {
  return SELECTABLE_LAYOUTS.find(layout => layout.id === layoutId);
}
